import type { ComponentPropsWithoutRef, ReactNode } from "react";

import { cx } from "../../lib/cx";
import { Icon, type IconName } from "../core/Icon";
import s from "./Alert.module.css";

export type AlertTone = "success" | "info" | "warning" | "danger";

const ICONO: Record<AlertTone, IconName> = {
  success: "check-circle-2",
  info: "info",
  warning: "alert-triangle",
  danger: "alert-circle",
};

export interface AlertProps extends Omit<ComponentPropsWithoutRef<"div">, "title"> {
  tone?: AlertTone;
  title?: string;
  children?: ReactNode;
  /** `alert` interrumpe al lector de pantalla; `status` espera. Default: alert para danger, status para el resto. */
  role?: "alert" | "status";
  onDismiss?: () => void;
  /** Otro icono que el del tono (`mail-check` para "te enviamos el enlace"). */
  icon?: IconName;
  /** Una sola línea, chica y del color del tono, con el icono centrado: el aviso adentro de un formulario. */
  compact?: boolean;
}

/** Un aviso en línea: qué pasó y qué hacer. Nunca "Oops". */
export function Alert({ tone = "info", title, children, role, onDismiss, icon, compact, className, ...rest }: AlertProps) {
  return (
    <div
      role={role ?? (tone === "danger" ? "alert" : "status")}
      className={cx(s.alert, className)}
      data-tone={tone}
      data-compact={compact || undefined}
      {...rest}
    >
      <span className={s.icono}>
        <Icon name={icon ?? ICONO[tone]} size={compact ? 14 : 18} strokeWidth={compact ? 2 : 1.5} />
      </span>
      <div className={s.textos}>
        {title ? <div className={s.titulo}>{title}</div> : null}
        {children ? <div className={s.cuerpo}>{children}</div> : null}
      </div>
      {onDismiss ? (
        <button type="button" aria-label="Cerrar" className={s.cerrar} onClick={onDismiss}>
          <Icon name="x" size={15} />
        </button>
      ) : null}
    </div>
  );
}
